import { z } from 'zod';
import { productSizeOptionSchema } from './catalog';
import { UUID_PATTERN, normalizedUuidSchema as uuidSchema } from './common';
import { designDocumentSchema } from './design';
import { guestContactSchema, guestKeySchema } from './guest-contact';
import { refineDestinationSizes, sizeTotalSchema } from './quote-sizes';
import { issuedQuoteVersionSchema } from './quote-versions';
import {
  type QuoteRequestStatus,
  quoteCalendarDateSchema,
  quoteOptionalTextSchema,
  quotePriceTierSchema,
  quoteQuantitySchema,
  quoteRequiredTextSchema,
  refineDestinationTotal,
} from './quotes';
import {
  destinationListSchema,
  guestDestinationInputSchema,
  shippingFeeSettingsSchema,
} from './shipping';

// ---------------------------------------------------------------------------
// 비회원 견적 요청(SUPER-83) — 요청 폼·제출·견적 링크 화면·연결(claim)·관리자 링크 복사 계약.
// 연락 방법·휴대폰 정규화·비회원 이메일·비회원 키는 guest-contact.ts, 링크 서명 함수는 서버 전용 진입점(`./server`)에 있다.
// ---------------------------------------------------------------------------

/**
 * 개인정보 수집·이용 동의 문구 버전(결정 G4) — 비회원 요청 행 `guest_consent_version` 에 남는다. 확정 전이라 초안 접두(`draft-`)다.
 * `-b`: 수집 항목에 배송지 주소(우편번호·기본주소)를 더했다(SUPER-86).
 */
export const GUEST_QUOTE_CONSENT_VERSION = 'draft-2026-10-b';

/** 남용 방지 한도(결정 G16) — Redis 고정 창으로 센다. 넘으면 429. */
export const GUEST_QUOTE_SUBMITS_PER_HOUR_PER_IP = 5;
export const GUEST_QUOTE_SUBMITS_PER_DAY_PER_CONTACT = 3;
export const GUEST_ASSET_PRESIGNS_PER_HOUR = 20;
export const GUEST_ASSET_PRESIGNS_PER_HOUR_PER_IP = 60;

/** 견적 링크 경로 접두(결정 G8) — 웹 라우트 `/q/:quoteRequestId/:signature`. */
export const GUEST_QUOTE_LINK_PATH_PREFIX = '/q';
/** 링크 서명 — HMAC-SHA256 32바이트의 base64url(패딩 없음) = 43자. 서명은 서버만 만든다(`signGuestQuoteLink`). */
export const GUEST_QUOTE_LINK_SIGNATURE_PATTERN = /^[A-Za-z0-9_-]{43}$/;
/** 링크 경로 전체 — `/q/{uuid}/{서명 43자}`. 앞뒤에 다른 경로가 붙으면 받지 않는다. */
export const GUEST_QUOTE_LINK_PATH_PATTERN = new RegExp(
  `^${GUEST_QUOTE_LINK_PATH_PREFIX}/${UUID_PATTERN}/[A-Za-z0-9_-]{43}$`,
);
/** 링크 경로 조립 — api(제출 응답·관리자 링크 복사)와 worker(메일)가 이 함수로만 경로를 만든다. */
export const guestQuoteLinkPath = (quoteRequestId: string, signature: string): string =>
  `${GUEST_QUOTE_LINK_PATH_PREFIX}/${quoteRequestId}/${signature}`;

/**
 * 링크 공유 안내(사용자 결정 2026-10-16) — 서명 링크를 가진 고객 계정은 누구나 견적을 연결하고 주문할 수 있다(결정 G10).
 * web 의 링크 화면(검토 중·발행됨)·요청 완료 화면과 worker 의 비회원 메일 꼬리가 이 한 상수를 글자 그대로 보여 준다.
 */
export const GUEST_QUOTE_LINK_NOTICE =
  '이 링크를 통해 누구나 해당 견적으로 주문할 수 있습니다. 다른 사람에게 공유되지 않도록 안전하게 보관해 주세요.';

/** 요청 폼 조회 입력 — 비회원은 저장한 디자인이 없어 상품·색상으로 묻는다. */
export const guestQuoteRequestFormInputSchema = z.strictObject({
  productId: uuidSchema,
  colorOptionId: uuidSchema,
});
export type GuestQuoteRequestFormInput = z.infer<typeof guestQuoteRequestFormInputSchema>;

/** 요청 폼 — 회원 `quoteRequestFormSchema` 와 같은 칸 타입. 디자인 id·담당자 기본값·원본 디자인 썸네일은 없다. */
export const guestQuoteRequestFormSchema = z.strictObject({
  productId: uuidSchema,
  productName: z.string(),
  colorOptionId: uuidSchema,
  colorName: z.string(),
  minOrderQty: quoteQuantitySchema,
  priceTiers: z.array(quotePriceTierSchema),
  printMethods: z.array(z.strictObject({ id: uuidSchema, name: z.string() })),
  /** 사이즈 옵션 — 없으면 빈 배열(사이즈 없는 상품). */
  sizeOptions: z.array(productSizeOptionSchema),
  /** 요청 순간의 배송비 설정(spec §6-1 임시 견적) — 제출 때는 서버가 다시 읽는다. */
  shippingFeeSettings: shippingFeeSettingsSchema,
});
export type GuestQuoteRequestForm = z.infer<typeof guestQuoteRequestFormSchema>;

export const GUEST_QUOTE_PRIVACY_CONSENT_REQUIRED_MESSAGE = '개인정보 수집·이용에 동의해 주세요.';
export const GUEST_QUOTE_DESIGN_MISMATCH_MESSAGE =
  '디자인의 상품·색상이 요청한 상품·색상과 다릅니다.';

/**
 * 비회원 요청 제출(결정 G5·G12). 디자인 문서를 그대로 싣고 서버가 회원 저장과 같은 검증을 거쳐 사본으로 고정한다.
 * 칸 규칙은 회원 `quoteRequestInputSchema` 와 같다(희망 납기·회사명·메모는 null 로 비운다). 배송지는 우편번호·기본주소·수량만
 * 받는다(SUPER-86 D8 — 받는 분·연락처·상세주소는 주문 때). 견적 수량은 받지 않고 서버가 배송지 수량 합계로 계산한다(spec §5-1).
 * 동의는 true 리터럴이라 체크 없이는 통과하지 않는다. 문서의 상품·색상은 요청의 상품·색상과 같아야 한다.
 */
export const guestQuoteRequestInputSchema = z
  .strictObject({
    guestKey: guestKeySchema,
    productId: uuidSchema,
    colorOptionId: uuidSchema,
    design: designDocumentSchema,
    /** 배송지(spec D8) — 우편번호·기본주소·수량만. */
    destinations: destinationListSchema(guestDestinationInputSchema),
    printMethodId: uuidSchema,
    desiredDate: quoteCalendarDateSchema.nullable(),
    companyName: quoteOptionalTextSchema(100),
    contactName: quoteRequiredTextSchema.max(50),
    memo: quoteOptionalTextSchema(1000),
    contact: guestContactSchema,
    privacyConsent: z.literal(true, { message: GUEST_QUOTE_PRIVACY_CONSENT_REQUIRED_MESSAGE }),
  })
  .superRefine((input, ctx) => {
    if (
      input.design.productId !== input.productId ||
      input.design.colorOptionId !== input.colorOptionId
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['design'],
        message: GUEST_QUOTE_DESIGN_MISMATCH_MESSAGE,
      });
    }
    refineDestinationTotal(input.destinations, ctx);
    refineDestinationSizes(input.destinations, ctx);
  });
export type GuestQuoteRequestInput = z.infer<typeof guestQuoteRequestInputSchema>;

/** 제출 결과 — 접수번호와 견적 링크 경로(완료 화면이 보여 주고 즐겨찾기를 안내한다). */
export const guestQuoteRequestResultSchema = z.strictObject({
  quoteRequestId: uuidSchema,
  quoteNumber: z.string(),
  linkPath: z.string().regex(GUEST_QUOTE_LINK_PATH_PATTERN),
});
export type GuestQuoteRequestResult = z.infer<typeof guestQuoteRequestResultSchema>;

/** 링크 화면·연결 입력 — 웹 라우트 파라미터 그대로. 견적 id 는 소문자로 맞춘다(서명은 DB 의 소문자 id 로 만든다). */
export const guestQuoteLinkParamsSchema = z.strictObject({
  quoteRequestId: uuidSchema,
  signature: z.string().regex(GUEST_QUOTE_LINK_SIGNATURE_PATTERN),
});
export type GuestQuoteLinkParams = z.infer<typeof guestQuoteLinkParamsSchema>;

/** 연결(claim, 결정 G10) — 링크와 같은 입력. 결과는 연결된 견적 id(웹이 주문하기·내 견적으로 보낸다). */
export const claimGuestQuoteInputSchema = guestQuoteLinkParamsSchema;
export type ClaimGuestQuoteInput = GuestQuoteLinkParams;
export const claimGuestQuoteResultSchema = z.strictObject({ quoteRequestId: uuidSchema });
export type ClaimGuestQuoteResult = z.infer<typeof claimGuestQuoteResultSchema>;

/** 링크 화면 '끝남' 상태(결정 G9) — 반려·취소·만료·거절. */
export const GUEST_QUOTE_CLOSED_STATUSES = [
  'REJECTED',
  'CANCELED',
  'EXPIRED',
  'DECLINED',
] as const satisfies readonly QuoteRequestStatus[];
export type GuestQuoteClosedStatus = (typeof GUEST_QUOTE_CLOSED_STATUSES)[number];

/** 검토 중·발행됨이 함께 보여 주는 요약 — 연락처·배송지·메모는 싣지 않는다(링크 유출 대비). */
const guestQuoteSummaryShape = {
  quoteNumber: z.string(),
  productName: z.string(),
  colorName: z.string(),
  quantity: quoteQuantitySchema,
  /** 사이즈별 총 수량 — 사이즈 없는 상품이면 빈 배열. */
  sizeTotals: z.array(sizeTotalSchema),
  requestedAt: z.iso.datetime(),
  /** 견적 사본 썸네일 표시 주소 — 아직 그리지 않았으면 null. */
  thumbnailUrl: z.string().nullable(),
  /** 상품 대표 이미지 표시 주소 — 일반 견적에만 싣는다(디자인 썸네일이 없어 이것을 보인다, 일반 견적 설계 §6). 디자인 견적이거나 없으면 null. */
  productImageUrl: z.string().nullable(),
};

/**
 * 링크 화면(결정 G9) — 상태별로 보여 줄 것만 싣는다. 연결된 견적은 내용 없이 `CLAIMED` 하나다.
 * 서명이 틀리거나 없는 견적은 이 응답이 아니라 하나의 "찾을 수 없음" 오류다(존재 여부를 알려 주지 않는다).
 */
export const guestQuoteViewSchema = z.discriminatedUnion('state', [
  z.strictObject({ state: z.literal('REVIEWING'), ...guestQuoteSummaryShape }),
  z.strictObject({
    state: z.literal('ISSUED'),
    ...guestQuoteSummaryShape,
    issued: issuedQuoteVersionSchema,
  }),
  z.strictObject({
    state: z.literal('CLOSED'),
    quoteNumber: z.string(),
    status: z.enum(GUEST_QUOTE_CLOSED_STATUSES),
  }),
  z.strictObject({ state: z.literal('CLAIMED') }),
]);
export type GuestQuoteView = z.infer<typeof guestQuoteViewSchema>;
export type GuestQuoteViewState = GuestQuoteView['state'];

/** 관리자 [링크 복사](결정 G14) — `APP_WEB_URL` + 링크 경로. */
export const adminGuestQuoteLinkSchema = z.strictObject({ url: z.string() });
export type AdminGuestQuoteLink = z.infer<typeof adminGuestQuoteLinkSchema>;
